import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { isValidEmail } from '@/lib/utils';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { roles } from '../../utils/constants';
import { useSendInvitationToUserMutation, googleAdsAccountUsersQueryOptions, organizationQueryOptions } from '@/lib/query-options';
import { useToast } from '@/hooks/useToast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { organizationDataAtom } from '@/store';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: number;
}

export const InviteMemberModal = ({ isOpen, onClose, organizationId }: InviteMemberModalProps) => {
  const [email, setEmail] = useState<string>('');
  const [selectedGoogleAdsUser, setSelectedGoogleAdsUser] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<'google-ads-users' | 'email'>('google-ads-users');
  const [selectedRole, setSelectedRole] = useState<string>('member');
  
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const inviteMutation = useSendInvitationToUserMutation();
  const organizationData = useAtomValue(organizationDataAtom);
  
  // Fetch Google Ads account users
  const { data: googleAdsUsers } = useQuery(googleAdsAccountUsersQueryOptions());
  
  // Fetch organization members
  const { data: organization } = useQuery({
    ...organizationQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });
  
  // Filter Google Ads users who are not already in the organization
  const availableGoogleAdsUsers = useMemo(() => {
    if (!googleAdsUsers || !organization) return [];
    
    const existingEmails = new Set(organization.users?.map(u => u.email.toLowerCase()) || []);
    const filtered = googleAdsUsers.filter(user => !existingEmails.has(user.email_address.toLowerCase()));
    
    // Set default to first user if not already selected
    if (filtered.length > 0 && !selectedGoogleAdsUser) {
      setSelectedGoogleAdsUser(filtered[0].email_address);
    }
    
    return filtered;
  }, [googleAdsUsers, organization, selectedGoogleAdsUser]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    let inviteEmail = '';
    
    if (activeTab === 'google-ads-users') {
      if (!selectedGoogleAdsUser) {
        setError('Please select a user from the dropdown');
        return;
      }
      inviteEmail = selectedGoogleAdsUser;
    } else {
      // Tell-on-click per-field validation (#5857): mark the email field instead
      // of an aggregate inline message.
      if (!email.trim()) {
        setEmailError('Required');
        return;
      }
      if (!isValidEmail(email)) {
        setEmailError('Enter a valid email');
        return;
      }
      inviteEmail = email.trim();
    }

    setError('');

    inviteMutation.mutate(
      {
        email: inviteEmail,
        role: selectedRole,
        organizationId,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Success',
            description: 'Invitation sent successfully!',
          });
          queryClient.invalidateQueries({ queryKey: ['organizations'] });
          setEmail('');
          setSelectedGoogleAdsUser('');
          setSelectedRole('member');
          onClose();
        },
        onError: (error) => {
          let errorMessage = 'Failed to send invitation';
          
          // Handle different error formats
          if (typeof error === 'string') {
            errorMessage = error;
          } else if (error?.message) {
            if (error.message.includes('Failed to fetch')) {
              errorMessage = 'Network error. Please check your connection and try again.';
            } else if (error.message.includes('already a member') || error.message.includes('AlreadyOrganizationMemberException')) {
              errorMessage = 'This user is already a member of the organization. Try a different email address.';
            } else if (error.message.includes('User not found')) {
              errorMessage = 'No user found with this email address.';
            } else if (error.message.includes('Invalid email')) {
              errorMessage = 'Please enter a valid email address.';
            } else {
              errorMessage = error.message;
            }
          } else if ((error as any)?.detail) {
            errorMessage = (error as any).detail;
          } else if ((error as any)?.error) {
            errorMessage = (error as any).error;
          }
          
          toast({
            title: 'Invitation Failed',
            description: errorMessage,
            variant: 'destructive',
          });
          // Don't clear the form on error - let user fix the issue
        },
      },
    );
  };

  const handleClose = () => {
    setEmail('');
    setSelectedGoogleAdsUser('');
    setError('');
    setEmailError(undefined);
    setActiveTab('google-ads-users');
    setSelectedRole('member');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className='max-h-[90vh] w-full overflow-hidden rounded-lg shadow-lg' aria-describedby={undefined}>
        <DialogHeader className='p-2'>
          <DialogTitle className='text-2xl font-semibold'>Invite Member</DialogTitle>
        </DialogHeader>
        <div className='px-3 mt-2 space-y-6'>
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'google-ads-users' | 'email')} className='w-full'>
            <TabsList className='grid w-full grid-cols-2'>
              <TabsTrigger value='google-ads-users'>Google Ads Account Users</TabsTrigger>
              <TabsTrigger value='email'>Email</TabsTrigger>
            </TabsList>
            
            <TabsContent value='google-ads-users' className='space-y-4 mt-4'>
              <div className='flex items-start space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg'>
                <CheckCircle className='h-5 w-5 text-green-600 flex-shrink-0 mt-0.5' />
                <p className='text-gray-900'>
                  The following emails have access to this Google Ads account and can view all existing campaigns.
                </p>
              </div>
              
              <div className='flex gap-4'>
                <div className='flex-1'>
                  <h3 className='font-medium text-gray-900 mb-2'>Google Ads users</h3>
                  <Select value={selectedGoogleAdsUser} onValueChange={setSelectedGoogleAdsUser}>
                    <SelectTrigger className='w-full border border-slate-500 text-base'>
                      <SelectValue placeholder='Select a user to invite' />
                    </SelectTrigger>
                    <SelectContent>
                      {availableGoogleAdsUsers.length > 0 ? (
                        availableGoogleAdsUsers.map((user) => (
                          <SelectItem key={user.user_id} value={user.email_address} className='text-base'>
                            {user.email_address}
                          </SelectItem>
                        ))
                      ) : (
                        <div className='px-2 py-4 text-gray-900 text-center'>
                          All Google Ads users are already members of this organization
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className='w-1/3'>
                  <h3 className='font-medium text-gray-900 mb-2'>Role</h3>
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger className='w-full border border-slate-500 text-base'>
                      <SelectValue placeholder='Select a role' />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role.value} value={role.value} className='text-base'>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value='email' className='space-y-4 mt-4'>
              <div
                className='border-l-4 rounded-lg p-4'
                style={{
                  backgroundColor: '#FEF3C7',
                  borderLeftColor: '#A16207',
                  borderTop: '1px solid #FDE68A',
                  borderRight: '1px solid #FDE68A',
                  borderBottom: '1px solid #FDE68A'
                }}
              >
                <div className='flex items-start gap-3'>
                  <AlertCircle className='h-5 w-5 flex-shrink-0 mt-0.5' style={{ color: '#A16207' }} />
                  <div className='text-gray-900'>
                    Ensure users are in your{' '}
                    <a
                      href='https://ads.google.com'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-blue-600 hover:text-blue-700 underline'
                    >
                      Google Ads account
                    </a>
                    {' '}first or they will not be able to view campaigns.
                  </div>
                </div>
              </div>
              
              <div className='flex gap-4'>
                <div className='flex-1'>
                  <div className='flex items-center justify-between mb-2'>
                    <h3 className='font-medium text-gray-900'>Email</h3>
                    <FieldError message={emailError} />
                  </div>
                  <div className='relative'>
                    <Input
                      type='email'
                      placeholder='example@mail.com'
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError(undefined);
                      }}
                      error={!!emailError}
                      className={
                        emailError
                          ? 'rounded-lg pr-8'
                          : '!ring !ring-transparent border border-slate-500 rounded-lg'
                      }
                    />
                    <InputAdornment error={!!emailError} />
                  </div>
                </div>
                
                <div className='w-1/3'>
                  <h3 className='font-medium text-gray-900 mb-2'>Role</h3>
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger className='w-full border border-slate-500 text-base'>
                      <SelectValue placeholder='Select a role' />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role.value} value={role.value} className='text-base'>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <div className='space-y-4'>
            
            {error && <p className='text-red-500 text-sm'>{error}</p>}
            
            <div className='pt-4'>
              <Button 
                type='submit' 
                size='lg'
                className='w-full py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed' 
                onClick={handleSubmit}
                disabled={inviteMutation.isPending || (activeTab === 'google-ads-users' && availableGoogleAdsUsers.length === 0)}
              >
                <span>{inviteMutation.isPending ? 'SENDING...' : 'SEND INVITE'}</span>
                {!inviteMutation.isPending && <ArrowRight className='h-4 w-4' />}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
